import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../contexts/AuthContext';
import { OrganizationContext } from '../../App';
import { userAPI } from '../../services/api';
import PageTitle from '../shared/PageTitle';
import { useSettings } from '../../contexts/SettingsContext';

const DirectoryPage = () => {
  const { currentUser, loading: authLoading } = useContext(AuthContext);
  const orgContext = useContext(OrganizationContext);
  const orgDomain = orgContext?.orgDomain;
  const prefix = orgDomain ? `/${orgDomain}` : '';
  const navigate = useNavigate();
  const { settings } = useSettings();

  // Restrict access to admin and owner
  useEffect(() => {
    if (!authLoading && currentUser) {
      if (!['admin', 'owner'].includes(currentUser.role)) {
        switch (currentUser.role) {
          case 'guard':
            navigate(`${prefix}/guard/dashboard`, { replace: true });
            break;
          case 'maintenance':
            navigate(`${prefix}/maintenance/dashboard`, { replace: true });
            break;
          case 'superadmin':
            navigate(`/superadmin/dashboard`, { replace: true });
            break;
          default:
            navigate(`${prefix}/login`, { replace: true });
        }
      }
    }
  }, [authLoading, currentUser, navigate, prefix]);

  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('residents');
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  // Fetch all users for this organization
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        setLoading(true);
        const response = await userAPI.getAllUsers();
        setUsers(response.data);
        setError(null);
      } catch (err) {
        console.error(err);
        setError('Failed to load users.');
      } finally {
        setLoading(false);
      }
    };
    fetchUsers();
  }, []);

  // Reset pagination when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, searchTerm, roleFilter]);

  // Filter users by tab, search, and role filter
  const filtered = users.filter(u => {
    if (activeTab === 'residents' && u.role !== 'owner') return false;
    if (activeTab === 'staff' && !['guard', 'maintenance'].includes(u.role)) return false;
    if (activeTab === 'committee' && !u.committee_role) return false;
    if (activeTab === 'committee' && roleFilter !== 'all' && u.committee_role !== roleFilter) return false;
    if (activeTab === 'staff' && roleFilter !== 'all' && u.role !== roleFilter) return false;
    const term = searchTerm.toLowerCase();
    if (
      !u.name.toLowerCase().includes(term) &&
      !u.email.toLowerCase().includes(term) &&
      !(u.phone?.toString().toLowerCase().includes(term))
    ) return false;
    return true;
  });

  const totalPages = Math.ceil(filtered.length / itemsPerPage);
  const paginatedUsers = filtered.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

  if (authLoading || loading) {
    return (
      <div className="d-flex justify-content-center mt-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-fluid mt-4 activity-container">
        <div className="alert alert-danger">{error}</div>
      </div>
    );
  }

  // Get theme colors for styling
  const primaryColor = settings.theme_primary_color || '#0d6efd';
  const tabsBgColor = settings.tabs_bg_color || '#f8f9fa';
  const tabsTextColor = settings.tabs_text_color || '#212529';
  const tabsActiveBgColor = settings.tabs_active_bg_color || primaryColor;
  const tabsActiveTextColor = settings.tabs_active_text_color || '#ffffff';
  const tabsHoverBgColor = settings.tabs_hover_bg_color || '#f1f1f1';
  const tabsHoverTextColor = settings.tabs_hover_text_color || primaryColor;

  return (
    <div className="container-fluid mt-4 activity-container">
      <PageTitle icon="fa-address-book" title="Directory" />

      {/* Enhanced Tabs with Theme Colors */}
      <div className="directory-tabs-container mb-4">
        <ul className="nav nav-tabs directory-tabs" style={{
          '--tabs-bg-color': tabsBgColor,
          '--tabs-text-color': tabsTextColor,
          '--tabs-active-bg-color': tabsActiveBgColor,
          '--tabs-active-text-color': tabsActiveTextColor,
          '--tabs-hover-bg-color': tabsHoverBgColor,
          '--tabs-hover-text-color': tabsHoverTextColor
        }}>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'residents' ? 'active' : ''}`}
              onClick={() => setActiveTab('residents')}
            >
              <i className="fas fa-users me-2"></i>
              Society Residents
            </button>
          </li>
          <li className="nav-item">
            <button
              className={`nav-link ${activeTab === 'staff' ? 'active' : ''}`}
              onClick={() => setActiveTab('staff')}
            >
              <i className="fas fa-user-tie me-2"></i>
              Society Staff
            </button>
          </li>
          {['admin', 'owner'].includes(currentUser.role) && (
            <li className="nav-item">
              <button
                className={`nav-link ${activeTab === 'committee' ? 'active' : ''}`}
                onClick={() => setActiveTab('committee')}
              >
                <i className="fas fa-user-cog me-2"></i>
                Working Committee
              </button>
            </li>
          )}
        </ul>
      </div>

      {/* Search and filter */}
      <div className="row mb-3">
        <div className="col-md-6 mb-2">
          <input
            type="text"
            className="form-control"
            placeholder="Search by name or phone"
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>
        {activeTab === 'staff' && (
          <div className="col-md-3 mb-2">
            <select
              className="form-select"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
            >
              <option value="all">All Staff</option>
              <option value="guard">Guards</option>
              <option value="maintenance">Maintenance</option>
            </select>
          </div>
        )}
        {activeTab === 'committee' && (
          <div className="col-md-3 mb-2">
            <select
              className="form-select"
              value={roleFilter}
              onChange={e => setRoleFilter(e.target.value)}
            >
              <option value="all">All Positions</option>
              <option value="President">President</option>
              <option value="Vice President">Vice President</option>
              <option value="Secretary">Secretary</option>
              <option value="Treasurer">Treasurer</option>
              <option value="Governing Member">Governing Member</option>
            </select>
          </div>
        )}
      </div>

      {/* User table */}
      <div className="table-responsive">
        <table className="table table-hover table-striped">
          <thead>
            <tr>
              <th>Name</th>
              {currentUser.role == "admin" && (
                <>
                  <th>Email</th>
                </>
              )}
              <th>Phone</th>
              {activeTab === 'residents' && <th>Unit (Type)</th>}
              {activeTab === 'committee' && <th>Unit (Type)</th>}
              <th>{activeTab === 'committee' ? 'Position' : 'Role'}</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length > 0 ? (
              paginatedUsers.map(u => {
                const isSelf = currentUser && u.id === currentUser.id;
                const isAdmin = currentUser && currentUser.role === 'admin';
                const canShowContact = isSelf || isAdmin || settings.show_owner_contact;
                return (
                  <tr key={u.id}>
                    <td>{u.name}</td>
                    {currentUser.role == "admin" && (
                      <>
                        <td>{activeTab === 'residents' ? (canShowContact ? u.email : 'Hidden') : u.email}</td>
                      </>
                    )}
                    <td>{activeTab === 'residents' ? (canShowContact ? (u.phone || 'N/A') : 'Hidden') : (u.phone || 'N/A')}</td>
                    {activeTab === 'residents' && <td>{u.unit_numbers_with_type || u.unit_numbers || 'N/A'}</td>}
                    {activeTab === 'committee' && <td>{u.unit_numbers_with_type || 'N/A'}</td>}
                    {activeTab === 'committee' ? (
                      <td>{u.committee_role}</td>
                    ) : (
                      <td>{u.role.charAt(0).toUpperCase() + u.role.slice(1)}</td>
                    )}
                  </tr>
                )
              })
            ) : (
              <tr>
                <td colSpan="5" className="text-center py-4 text-muted">
                  <i className="fas fa-building fa-3x mb-3 d-block"></i>
                  <p>No data found matching your criteria.</p>
                  <p>Try adjusting your search or filters.</p>
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination controls - only show if more than 1 page */}
      {totalPages > 1 && (
        <nav>
          <ul className="pagination justify-content-center">
            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage(p => Math.max(p - 1, 1))}
              >Previous</button>
            </li>
            <li className="page-item disabled">
              <span className="page-link">Page {currentPage} of {totalPages || 1}</span>
            </li>
            <li className={`page-item ${currentPage === totalPages || totalPages === 0 ? 'disabled' : ''}`}>
              <button
                className="page-link"
                onClick={() => setCurrentPage(p => Math.min(p + 1, totalPages))}
              >Next</button>
            </li>
          </ul>
        </nav>
      )}

      <style>{`
        .directory-tabs-container {
          background-color: var(--tabs-bg-color);
          border-radius: 12px;
          padding: 8px;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
        }

        .directory-tabs {
          border: none !important;
          margin-bottom: 0 !important;
        }

        .directory-tabs .nav-link {
          color: var(--tabs-text-color) !important;
          background-color: transparent !important;
          border: none !important;
          border-radius: 8px !important;
          margin-right: 4px;
          padding: 12px 20px;
          font-weight: 500;
          transition: all 0.2s ease;
          display: flex;
          align-items: center;
        }

        .directory-tabs .nav-link:hover:not(.active) {
          background-color: var(--tabs-hover-bg-color) !important;
          color: var(--tabs-hover-text-color) !important;
          transform: translateY(-1px);
        }

        .directory-tabs .nav-link.active {
          background-color: var(--tabs-active-bg-color) !important;
          color: var(--tabs-active-text-color) !important;
          font-weight: 600;
          box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
        }

        .directory-tabs .nav-link i {
          font-size: 0.9rem;
        }

        @media (max-width: 768px) {
          .directory-tabs-container {
            padding: 4px;
          }
          
          .directory-tabs .nav-link {
            padding: 10px 16px;
            font-size: 0.9rem;
          }
          
          .directory-tabs .nav-link i {
            display: none;
          }
        }
      `}</style>
    </div>
  );
};

export default DirectoryPage; 